import React, { useEffect, useState, useRef, forwardRef } from 'react';
import { affect_color_filter, convertObjectToArray, degToRad, setupStageTypography, calculateSliceSize, isNotEmpty, isColorBright, RGB_2_HEX, getSoundPropsById } from '../../lib/ep.js';
import Konva from 'konva';
import { Stage, Layer, Rect, Text, Group, Circle, Line, Shape, Path, Arc } from 'react-konva';
import { Howl } from 'howler';

import ActionButton from '../builder/ActionButton';
import Spinner from '../builder/Spinner';
import ButtonCollection from '../builder/ButtonCollection.js';

const white_color = '#FFFFFF';
const light_gray_color = '#EFEFEF';
const dark_gray_color = '#363636';
const black_color = '#000000';

const default_w = 600;
const default_h = 400;

let throttle_resize;

const RouletteCanvas = forwardRef((props, ref) => {
	const [ prize_won, setPrizeWon ] = useState(false);
	const [ is_spinning, setIsSpinning ] = useState(false);
	const [ load_confetti, setLoadConfetti ] = useState(false);
	const [ foo, setFoo ] = useState(0);
	const [ test_enabled, setTestEnabled ] = useState(false);
	const [ display_celebration, setDisplayCelebration ] = useState(true);

	const parent_element = (isNotEmpty(props, 'parent_id')) ? document.getElementById(props.parent_id).getBoundingClientRect() : false;
	const [ canvas_size_w, setCanvasSizeW ] = useState((parent_element) ? parent_element.width : default_w);
	const [ canvas_size_h, setCanvasSizeH ] = useState((parent_element) ? parent_element.height : default_h);

	const sounds = useRef({});

	const refs = useRef([]);
	const full_w = useRef(canvas_size_w);
	const full_h = useRef(canvas_size_h);
	const needs_recache = useRef(true);
	const first_loaded = useRef(false);
	const already_loaded = useRef(false);
	const font_loaded = useRef(false);
	const font_loaded_attempt = useRef(0);
	const force = useRef(0.5);
	const confetti_ref = useRef();
	const final_prizes = useRef(false);

	const is_builder = props?.is_builder ?? false;
	const is_demo = props?.is_demo ?? false;
	const layout_props = props?.layout_props ?? {};
	const stage = props?.stage ?? false;
	const is_playable = props?.is_playable ?? true;
	const button_area_height = (is_playable) ? 80 : 0;
	const ui_color = stage?.roulette_ui_color ?? layout_props.color.main_color;
	const is_ui_color_light = isColorBright(RGB_2_HEX(ui_color));
	const default_cache_props = { pixelRatio:2, imageSmoothingEnabled:true, offset:5, drawBorder:false };
	
	const silence = stage?.silence ?? false;

	const hide_final_prize_text = stage?.roulette_hide_final_prize_text ?? false;
	const roulette_initial_light_anim = stage?.roulette_initial_light_anim ?? 'none';	// 'none' || 'single' || 'double'

	let roulette_zoom_on_spin = stage?.roulette_zoom_on_spin ?? true;
	if(is_demo) roulette_zoom_on_spin = false;

	const keep_prize_in_screen = 6;		// seconds for PRIZE
	const keep_prize_in_screen_ko = 3;	// seconds for NO PRIZE
	const min_radius = 170;

	let prize_types = (props.prize_types != undefined) ? convertObjectToArray(props.prize_types) : false;
	let slices = props?.slices ?? false;
	if(!(prize_types && slices)) return null;

	const { typography, typography_weight } = setupStageTypography('generic', layout_props.text);

	let total_slices = useRef(slices.length);

	const getFinalSlice = (prize_type_id) => {
		if(prize_type_id == undefined) prize_type_id = final_prizes.current[0].key;
		let slices_with_price = slices.filter((obj) => (obj.prize == prize_type_id));
		const final_slice = slices_with_price[Math.floor(Math.random() * slices_with_price.length)];

		return final_slice;
	};

	const getRouletteRadius = () => {
		if(!is_playable) return min_radius;

		let _radius = Math.round(0.9 * Math.min(full_w.current, full_h.current) / 2) - button_area_height;
		return Math.max(_radius, min_radius);
	};

	let given_prize = useRef(getFinalSlice(props.given_prize));

	let radius = getRouletteRadius();
	const logo_radius = Math.ceil(radius / 5);
	const num_lights = 28;
	const external_ring_delta = radius / 30;
	const pin_radius = external_ring_delta / 2;
	const lights_rotation = 360 / num_lights;
	let light_objects = [];
	for(let i = 0; i < num_lights; i++) { light_objects.push(i); }
	const y_displacement = 0;
	const unit = Math.max(0.5, radius / 400);

	const angle_per_slice = 360 / slices.length;
	const whole_roulette_rotation = -(angle_per_slice / 2 + 90);
	const slice_threshold = angle_per_slice / 2;
	let total_rotation = 0;
	let initial_force;
	let active_slice;
	let initial_text_font_size;
	let finished = false;
	const max_extra_rounds = 3;

	let spin_anim;
	let win_lights_anim_init;
	let win_lights_anim;

	const is_final = (prize_won !== false && display_celebration) ? true : false;
	const final_prize_image =  props.indexed_images[`final_${props.given_prize}`];
	const logo_image =  props.indexed_images[`logo`];

	const logo_image_scale = (logo_image != undefined) ? 2 * logo_radius / Math.min(logo_image.image_w, logo_image.image_h) : 1;
	const prize_image_scale = (final_prize_image != undefined) ? 2 * radius / Math.min(final_prize_image.image_w, final_prize_image.image_h) : 1;

	const font_base_to_calc = 20;
	const calculateFontBase = () => {
		won_prize_text_font_size.current = font_base_to_calc * 2.5;
		return font_base_to_calc;
	};

	const font_sizer = -1.5 * (parseInt(stage?.roulette_font_size ?? 3) - 5);
	//const font_sizer = 15 - 3 * parseInt(stage?.roulette_font_size ?? 3);
	let won_prize_text_font_size = useRef(0);
	let text_font_size = useRef(font_base_to_calc);
	initial_text_font_size = calculateFontBase();

	prize_types.map((prize, p) => {
		let is_color_bright;
		if(prize.color.indexOf('rgba') != -1) {
			const color_parts = convertRGBAtoArrayParts(prize.color);

			// if the color has opacity less than 30%, we consider the color to be dark, so white text will be applied:
			// NOTE: if previous statement is true, and the roulette is over a light background, this might end up in non-well readable texts
			// (i.e. white text color over a very light background). Maybe we should rethink the situation in these (strange) cases.
			is_color_bright = (parseFloat(color_parts[4]) < 0.3) ? false : isColorBright(RGB_2_HEX(prize.color));
		} else {
			is_color_bright = isColorBright(RGB_2_HEX(prize.color));
		}
		let shader_direction = (is_color_bright) ? -1 : 1;

		prize_types[p].name = prize_types[p].name;
		prize_types[p].text_color = (is_color_bright) ? black_color : white_color;
		prize_types[p].color_hover = affect_color_filter(0.15 * shader_direction, prize.color);
		prize_types[p].color_final_hover = affect_color_filter(0.35 * shader_direction, prize.color);
		if(prize.full_given_prize_image != undefined && prize.full_given_prize_image != '') prize_types[p].full_given_prize_image = prize.full_given_prize_image;
	});

	const calculateTextSizes = (text, force_execution, font_size, useActualBoundBox) => {
		if(force_execution == undefined) force_execution = false;
		if(useActualBoundBox == undefined) useActualBoundBox = true;
		if(!force_execution && font_loaded.current) return false;

		if(font_size == undefined) font_size = initial_text_font_size;
		if(text == undefined) text = "Tmpl";

		let _canvas = document.createElement('canvas');
		let ctx = _canvas.getContext('2d');
		ctx.font = `${typography_weight} ${font_size}px ${typography}`;

		const measures = ctx.measureText(text);

		ctx = null;
		_canvas = null;

		return {
			width:measures.width,
			height:(useActualBoundBox) ? (measures.actualBoundingBoxAscent + measures.actualBoundingBoxDescent) : (measures.fontBoundingBoxAscent + measures.fontBoundingBoxDescent),
		};
	};

	const test_measure = calculateTextSizes();
	const text_width_max = useRef(test_measure.width);
	const text_height_max = useRef(test_measure.height);

	useEffect(() => {
		detectImagesToLoad();
		if(props.slices.length != total_slices.current) {
			total_slices.current = props.slices.length;
			calculateTextScale();
		}

		needs_recache.current = true;

		if(given_prize.current != undefined && props.given_prize != given_prize.current.prize) {
			given_prize.current = getFinalSlice(props.given_prize);
			const given_prize_type = prize_types.find((p) => ( p.key == props.given_prize));

			if(isNotEmpty(given_prize_type, 'full_given_no_prize_image') && props.given_prize == 0) {
				setDisplayCelebration(false);
			}
		}

		setFoo(foo + 1);
	}, [ props ]);

	useEffect(() => {
		generateCache();
		calculateTextScale();
	}, [ props.stage.roulette_font_size, props.stage.roulette_font_family ]);

	const allReadyToGo = () => {
		if(font_loaded.current == true && already_loaded.current == true) return;

		animateExternalLightsInit();

		generateCache(() => {
			if(is_playable) {
				animateWholeRoulette('in');
			} else {
				spinDemoRoulette();
				animateDemoRoulette();
			}

			already_loaded.current = true;
		});
	};

	const detectImagesToLoad = () => {
		const has_images = prize_types.filter((obj) => (isNotEmpty(obj, 'image')) ? true : false);
		if(has_images.length == 0 && stage.roulette_logo == undefined) {
			needs_recache.current = true;
			final_prizes.current = prize_types;
		}

		allReadyToGo();
	};

	const loadFontFamilyAndProcessTextWidth = () => {
		if(font_loaded.current) return false;

		//whenFontIsLoaded(() => {
			let pt_textWidth = 0;
			let pt_textHeight = 0;
			prize_types.map((pt) => {
				const _t = calculateTextSizes(pt.name);
				if(_t.width > pt_textWidth) pt_textWidth = _t.width;
				if(_t.height > pt_textHeight) pt_textHeight = _t.height;
			});
			text_width_max.current = pt_textWidth;
			text_height_max.current = pt_textHeight;

			calculateTextScale(true);
		//});
	};

	const getAvailableTextWidth = () => {
		const delta_ini = logo_radius + slices.length / 2;
		const delta_end = 10;
		return {
			delta_ini,
			delta_end,
			available_w:radius - (delta_ini + delta_end),
		};
	};

	const calculateTextScale = (force_execution = false) => {
		if(!force_execution && !font_loaded.current) return false;

		const text_box = getAvailableTextWidth();
		const text_scale_w = Math.min(1, text_box.available_w / text_width_max.current);
		//const pos_to_get_height = text_box.delta_ini + (text_box.available_w - text_width_max.current * text_scale_w) / 2;
		//const text_available_height = 2 * (Math.tan(degToRad(angle_per_slice / 2)) * pos_to_get_height);
		//const text_scale_h = text_available_height / text_height_max.current;

		const canvas_scale = Math.min(radius / min_radius, 0.9 * text_scale_w);
		text_font_size.current = Math.floor(initial_text_font_size * canvas_scale);

		if(!font_loaded.current) {
			font_loaded.current = true;
			needs_recache.current = true;
			allReadyToGo();
			return;
		}

		needs_recache.current = true;
		setFoo(foo + 1);
	};

	/*const whenFontIsLoaded = (cb) => {
		if(font_loaded_attempt.current >= 10) {
			cb();
			return;
		}

		if(calculateTextSizes().width != text_width_max.current) {
			cb();
		} else {
			setTimeout(() => {
				font_loaded_attempt.current += 1;
				whenFontIsLoaded(cb);
			}, 50);
		}
	};*/

	const calcRouletteContainer = () => {
		if(!first_loaded.current) first_loaded.current = true;

		const container_element = document.getElementById('gui_roulette_container').getBoundingClientRect();
		full_w.current = container_element.width;
		full_h.current = container_element.height;
		setCanvasSizeW(full_w.current);
		setCanvasSizeH(full_h.current);
		radius = getRouletteRadius();
		needs_recache.current = true;
		generateCache();
	};

	const adaptToContainer = () => {
		if(throttle_resize != undefined) clearTimeout(throttle_resize);

		throttle_resize = setTimeout(() => {
			calcRouletteContainer();
			calculateTextScale();
		}, 150);
	};

	const killAllFrameAnimations = () => {
		if(spin_anim != null && spin_anim.isRunning()) spin_anim.stop();
		if(win_lights_anim != null && win_lights_anim.isRunning()) win_lights_anim.stop();
		if(win_lights_anim_init != null && win_lights_anim_init.isRunning()) win_lights_anim_init.stop();
	};

	const generateCache = (cb) => {
		if(!needs_recache.current || refs.current['roulette_ref'] == undefined) return false;

		setTimeout(() => {
			if(refs.current['roulette_ref'] != undefined) {
				refs.current['roulette_ref'].cache({ ...default_cache_props, offset:Math.floor(radius * 0.1) });
			}

			if(refs.current['pointer_ref'] != undefined) {
				refs.current['pointer_ref'].cache(default_cache_props);
			}

			if(refs.current['logo_ref'] != undefined) {
				refs.current['logo_ref'].cache(default_cache_props);
			}

			if(refs.current['roulette_ui_ref'] != undefined) {
				refs.current['roulette_ui_ref'].cache(default_cache_props);
			}

			setFoo(foo + 1);
			needs_recache.current = false;

			if(cb != undefined) cb();
		}, 50);
	};

	const spinDemoRoulette = () => {
		initial_force = 15;
		setPrizeWon(false);
		spin_anim = new Konva.Animation(animateRouletteSpin, refs.current['roulette_ref']);
		spin_anim.start();
		const num_rounds = 100;
		programmaticAnimation(num_rounds * 360, 100 * num_rounds);

		document.querySelector('.demo_roulette .gui_game_layer').classList.add('is_loaded');
	};

	const spinRoulette = (final_rotation) => {
		spin_anim = new Konva.Animation(animateRouletteSpin, refs.current['roulette_ref']);
		initial_force = final_rotation;
		toggleZoomRoulette(true);
		setIsSpinning(true);
		setPrizeWon(false);
		spin_anim.start();
		programmaticAnimation(final_rotation, 7);
	};

	const getActiveSlice = () => {
		if(refs.current['active_slice_collider_ref'] == undefined) return false;

		const collider = refs.current['active_slice_collider_ref'].getClientRect();
		let active_slice = refs.current['roulette_ref'].getIntersection({ x:collider.x + collider.width / 2, y:collider.y + collider.height / 2 }, 'Group');
		return active_slice;
	};

	const programmaticAnimation = (final_rotation, duration) => {
		refs.current['roulette_ref'].to({
			duration:duration,
			rotation:final_rotation + whole_roulette_rotation,
			easing:Konva.Easings.StrongEaseOut,
			onFinish:() => {
				finished = true;
				spin_anim.stop();
				if(is_playable) {
					const _active = getActiveSlice();
					(parseInt(given_prize.current.prize) > 0) ? assignPrize(given_prize.current.prize) : oopsYouLose();
					
					if(_active != undefined && _active.attrs != undefined) {
						// If the user hides the browser tab very, very fast (before the actual prize returns from PHP)
						// the roulette breaks if executes this line to display the view change.
						// So we don't fire it.
						refs.current[_active.attrs.slice_ref].fillPriority('fill');
					}
					toggleZoomRoulette(false);
				}
			},
		});
	};

	const animateRouletteSpin = (frame) => {
		const _active = getActiveSlice();

		if(is_playable) {
			let angularVelocityChange = (initial_force * frame.timeDiff) / 1000;
			initial_force -= angularVelocityChange;
		}

		if(!finished && _active && (!active_slice || _active._id !== active_slice._id) && _active.attrs.slice_ref != undefined) {
			animatePointer();
			active_slice = _active;
		} else {
			return false;
		}
	};

	const animatePointer = () => {
		refs.current['pointer_ref'].rotation(-30);

		refs.current['pointer_ref'].to({
			duration:Math.min(1.2, initial_force),
			rotation:0,
			easing:Konva.Easings.ElasticEaseOut,
		});

		if(is_playable) play_click_fx();
	};

	const resetRoulette = () => {
		killAllFrameAnimations();

		if(is_builder) {
			setPrizeWon(false);
			if(confetti_ref != undefined && confetti_ref.current != undefined) confetti_ref.current.destroy_animation();

			refs.current['logo_ref'].scaleX(1);
			refs.current['logo_ref'].scaleY(1);

			if(refs.current['prize_text_ref'] != undefined) refs.current['prize_text_ref'].clearCache();
			refs.current['roulette_ref'].rotation(whole_roulette_rotation);
			total_rotation = 0;
			active_slice = false;
			setTestEnabled(false);
			setIsSpinning(false);
			animateWholeRoulette('in');
		}

		if(props.end_animation_cb != undefined) props.end_animation_cb();
	};

	const toggleZoomRoulette = (roulette_zoomed) => {
		if(roulette_zoom_on_spin === false || refs.current['whole_roulette_ref'] == undefined) return;

		refs.current['whole_roulette_ref'].to({
			scaleX:((roulette_zoomed) ? 1.8 : 1),
			scaleY:((roulette_zoomed) ? 1.8 : 1),
			rotation:0,
			y:canvas_size_h / 2 + ((roulette_zoomed) ? canvas_size_h * 0.33 : y_displacement),
			duration:0.5,
		});
	};

	const play_click_fx = () => {
		if(silence) return;
		sounds.current.roulette_click_fx.play();
	};

	const play_prize_fx = () => {
		if(silence) return;
		sounds.current.end_prize_fx.play();
	};

	const play_no_prize_fx = () => {
		if(silence) return;
		sounds.current.end_no_prize_fx.play();
	};

	const getPrizeType = (prize_type_id) => {
		return ((final_prizes.current !== false) ? final_prizes.current : prize_types).find((obj) => (obj.key == prize_type_id));
	};

	const assignPrize = (prize_type_id) => {
		setPrizeWon(getPrizeType(prize_type_id));
		animatePrize(false);
		play_prize_fx();
	};

	const oopsYouLose = () => {
		setPrizeWon(getPrizeType(0));
		animatePrize(true);
		play_no_prize_fx();
	};

	const animatePrize = (is_no_prize) => {
		if(is_no_prize == undefined) is_no_prize = false;

		killAllFrameAnimations();

		animateSunburstWhole();

		if(!is_no_prize) {
			animateSunburstRotate();
			animateExternalLights();
		}

		setTimeout(() => {
			animateSunburstBg();

			refs.current['logo_ref'].to({
				scaleX:0,
				scaleY:0,
				duration:0.8,
				easing:Konva.Easings.ElasticEaseIn,
			});
		}, 100);
	};

	const animateDemoRoulette = () => {
		refs.current['whole_roulette_ref'].y(canvas_size_h * 1.5).scaleX(1).scaleY(1).rotation(0);
		refs.current['whole_roulette_ref'].to({
			y:canvas_size_h * 0.85,
			duration:0.5,
			easing:Konva.Easings.EaseOut,
		});
	};

	const animateWholeRoulette = (direction) => {
		if(direction == undefined) direction = 'in';

		generateCache();

		if(refs.current['whole_roulette_ref'] == undefined) return;
		if(direction == 'in') refs.current['whole_roulette_ref'].scaleX(0).scaleY(0).rotation(120);
		refs.current['whole_roulette_ref'].to({
			rotation:(direction == 'in') ? 0 : -120,
			scaleX:(direction == 'in') ? 1 : 0,
			scaleY:(direction == 'in') ? 1 : 0,
			duration:0.5,
			easing:(direction == 'in') ? Konva.Easings.EaseOut : Konva.Easings.EaseIn,
			onFinish:() => {
				if(direction == 'in' && is_playable) {
					setLoadConfetti(true);
					if(confetti_ref != undefined && confetti_ref.current != undefined) confetti_ref.current.init_animation();
				}
				if(direction == 'out') resetRoulette();
			}
		});
	};

	const animateGiveImage = () => {
		if(display_celebration) animateExternalLights();

		refs.current['given_prize_ref'].scaleX(0).scaleY(0);
		refs.current['given_prize_ref'].to({
			scaleX:1,
			scaleY:1,
			duration:0.4,
			easing:Konva.Easings.EaseOut,
			onFinish:() => {
				if(confetti_ref != undefined && confetti_ref.current != undefined) confetti_ref.current.play_animation();
				setTimeout(() => {
					refs.current['given_prize_ref'].to({
						scaleX:1,
						scaleY:1,
						duration:0.4,
						easing:Konva.Easings.EaseOut,
						onFinish:() => {
							animateWholeRoulette('out');
						},
					});
				}, ((keep_prize_in_screen + ((display_celebration) ? 2 : -2)) * 1000));
			},
		});
	};

	const animateSunburstBg = () => {
		if(final_prize_image != undefined) {
			animateGiveImage();
			return;
		}
		if(refs.current['sunburst_bg_ref'] == undefined) return;

		refs.current['sunburst_bg_ref'].scaleX(0).scaleY(0);
		refs.current['sunburst_bg_ref'].to({
			scaleX:1,
			scaleY:1,
			duration:0.4,
			easing:Konva.Easings.EaseOut,
			onFinish:() => {
				if(confetti_ref != undefined && confetti_ref.current != undefined) confetti_ref.current.play_animation();
				animatePrizeText('in');
			},
		});
	};

	const animateSunburstWhole = () => {
		if(final_prize_image != undefined) return;
		if(refs.current['sunburst_ref'] == undefined) {
			setTimeout(() => {
				animateWholeRoulette('out');
			}, 2000);
			return;
		}

		const sunburst_svg_original_size = 600;

		refs.current['sunburst_ref'].scaleX(0).scaleY(0);
		refs.current['sunburst_ref'].to({
			scaleX:radius * 2 / sunburst_svg_original_size,
			scaleY:radius * 2 / sunburst_svg_original_size,
			duration:0.5,
			easing:Konva.Easings.EaseOut,
			onFinish:() => {
				refs.current['whole_sunburst_ref'].cache(default_cache_props);
				setTimeout(() => {
					animatePrizeText('out');
				}, (((given_prize.current.id != '0') ? keep_prize_in_screen : keep_prize_in_screen_ko) - 2) * 1000);
			},
		});
	};

	const animatePrizeText = (direction) => {
		if(final_prize_image != undefined) return;

		if(direction == undefined) direction = 'in';

		refs.current['prize_text_ref'].cache(default_cache_props);

		if(direction == 'in') refs.current['prize_text_ref'].scaleX(0).scaleY(0).rotation(5);
		refs.current['prize_text_ref'].to({
			scaleX:(direction == 'in') ? 1 : 0,
			scaleY:(direction == 'in') ? 1 : 0,
			duration:0.7,
			easing:(direction == 'in') ? Konva.Easings.BackEaseOut : Konva.Easings.BackEaseIn,
			onFinish:() => {
				animatePrizeTextRotation();
				if(direction == 'out') animateWholeRoulette('out');
			},
		});
	};

	const animatePrizeTextRotation = () => {
		if(final_prize_image != undefined) return;

		refs.current['prize_text_ref'].to({
			rotation:-5,
			duration:keep_prize_in_screen,
			easing:Konva.Easings.BackEaseInOut,
		});
	};

	const animateSunburstRotate = () => {
		if(final_prize_image != undefined) return;
		if(refs.current['whole_sunburst_ref'] == undefined) return;

		refs.current['whole_sunburst_ref'].rotation(0);
		refs.current['whole_sunburst_ref'].to({
			rotation:keep_prize_in_screen * 45,
			duration:keep_prize_in_screen,
		});
	};

	const animateExternalLights = () => {
		killAllFrameAnimations();

		win_lights_anim = new Konva.Animation(animateExternalLights_anim, refs.current['lights_on_ref']);
		win_lights_anim.start();
	};

	const animateExternalLights_anim = (frame) => {
		if(refs.current['lights_on_ref'] != undefined) {
			const new_rotation = lights_rotation * Math.floor((frame.time / 3) % 360 / lights_rotation);
			if(new_rotation == refs.current['lights_on_ref'].rotation()) return false;	// to prevent extra draws
			refs.current['lights_on_ref'].rotation(new_rotation);
			refs.current['final_items_ref'].draw();
		}
	};

	const animateExternalLightsInit = () => {
		killAllFrameAnimations();

		win_lights_anim_init = new Konva.Animation(animateExternalLightsInit_anim, refs.current['lights_on_ref']);
		win_lights_anim_init.start();
	};

	const animateExternalLightsInit_anim = (frame) => {
		if(refs.current['lights_on_ref'] != undefined) {
			const new_rotation = lights_rotation * Math.floor((frame.time / 30) % 360 / lights_rotation);
			if(new_rotation == refs.current['lights_on_ref'].rotation()) return false;	// to prevent extra draws
			refs.current['lights_on_ref'].rotation(new_rotation);
			refs.current['final_items_ref'].draw();
		}
	};

	const spinRouletteWithForce = () => {
		const additional_rounds = Math.ceil(force.current * max_extra_rounds);
		const rotation_to_slice = angle_per_slice * (given_prize.current.id - 1) + angle_per_slice / 2;
		let true_rotation = 360 - rotation_to_slice - angle_per_slice / 2 - slice_threshold / 2;
		true_rotation += slice_threshold * Math.random();
		const final_rotation = true_rotation - total_rotation;

		spinRoulette(final_rotation + (2 + additional_rounds) * 360);
	};

	const pointer_data = (width) => {
		const p_w = width / 2;	// pointer width (half)
		const p_h = p_w * 3.5;	// pointer height
		const b_01 = 0.552;		// bezier strength 1
		const b_01i = 1 - b_01;	// bezier strength 1 inversed
		const b_02 = 0.618;		// bezier strength 2

		return `M0-${p_w}c-${b_01 * p_w},0-${p_w},${b_01i * p_w}-${p_w},${p_w}c0,${2 * b_02 * p_w},${p_w},${p_h - p_w},${p_w},${p_h - p_w}S${p_w},${2 * b_02 * p_w},${p_w},0C${p_w}-${b_01 * p_w},${b_01 * p_w}-${p_w},0-${p_w}z`;
	};

	const slice_shape_props_clip = (context) => {
		context.beginPath();
		context.moveTo(0, 0);
		context.arc(0, 0, radius, 0, degToRad(angle_per_slice));
		context.closePath();
	};

	const triangle_shape_props = (context, shape) => {
		context.beginPath();
		context.moveTo(0, 0);
		context.lineTo(radius * 0.08, -radius * 0.045);
		context.lineTo(radius * 0.08, radius * 0.045);
		context.closePath();
		context.fillStrokeShape(shape);
	};

	const logo_shape_props_clip = (context) => {
		context.beginPath();
		context.moveTo(0, 0);
		context.arc(0, 0, logo_radius, 0, Math.PI * 2, false);
		context.closePath();
	};

	const full_shape_props_clip = (context) => {
		context.beginPath();
		context.moveTo(0, 0);
		context.arc(0, 0, radius, 0, Math.PI * 2, false);
		context.closePath();
	};

	(!first_loaded.current && document.getElementById('gui_roulette_container') != undefined) ? calcRouletteContainer() : generateCache();

	const font_offset_y = (text_font_size.current - font_sizer) / 2;

	const prize_line_height = 1;
	let prize_won_text_prop = false;
	let prize_font_offset_y = 0;
	if(prize_won !== false) {
		prize_won_text_prop = calculateTextSizes(prize_won.name, true, won_prize_text_font_size.current);
		prize_font_offset_y += prize_won_text_prop.height / 2;

		const padding_to_substract = 0;	// 15
		const num_lines_extra = Math.ceil(prize_won_text_prop.width / (radius * 2 - padding_to_substract)) - 1;
		prize_font_offset_y += num_lines_extra * prize_line_height * prize_won_text_prop.height / 2;
	}

	const text_box = getAvailableTextWidth();

	const ideal_radius = 300;
	const slice_sice = calculateSliceSize(ideal_radius, slices.length);

	const sunburst_data = `M7.48-1.48l286.86-56.73c-2.59-13.19-6.04-26.06-10.3-38.57L7.21-2.46c-0.11-0.31-0.23-0.61-0.38-0.9l262.41-129.04c-5.91-11.99-12.58-23.53-19.98-34.56L6.34-4.24c-0.18-0.27-0.39-0.53-0.6-0.77l220.03-192.53c-8.77-10.02-18.2-19.45-28.22-28.22L5.02-5.73c-0.25-0.22-0.5-0.42-0.77-0.6l162.72-242.94c-11.02-7.4-22.56-14.07-34.56-19.98L3.36-6.84c-0.29-0.14-0.59-0.27-0.9-0.38l94.32-276.83c-12.5-4.26-25.38-7.7-38.57-10.3L1.48-7.48C1.16-7.54,0.84-7.58,0.51-7.6l19.45-291.72C13.36-299.76,6.71-300,0-300s-13.36,0.24-19.95,0.68L-0.51-7.6c-0.33,0.02-0.66,0.06-0.97,0.12l-56.73-286.86c-13.19,2.59-26.06,6.04-38.57,10.3L-2.46-7.21c-0.31,0.11-0.61,0.23-0.9,0.38l-129.04-262.41c-11.99,5.91-23.53,12.58-34.56,19.98L-4.24-6.34c-0.27,0.18-0.53,0.39-0.77,0.6l-192.53-220.03c-10.02,8.77-19.45,18.2-28.22,28.22L-5.73-5.02c-0.22,0.25-0.42,0.5-0.6,0.77l-242.94-162.72c-7.4,11.02-14.07,22.56-19.98,34.56L-6.84-3.36c-0.14,0.29-0.27,0.59-0.38,0.9l-276.83-94.32c-4.26,12.5-7.7,25.38-10.3,38.57L-7.48-1.48c-0.06,0.32-0.1,0.64-0.12,0.97l-291.72-19.45C-299.76-13.36-300-6.71-300,0s0.24,13.36,0.68,19.95L-7.6,0.51c0.02,0.33,0.06,0.66,0.12,0.97l-286.86,56.73c2.59,13.19,6.04,26.06,10.3,38.57L-7.21,2.46c0.11,0.31,0.23,0.61,0.38,0.9l-262.41,129.04c5.91,11.99,12.58,23.53,19.98,34.56L-6.34,4.24c0.18,0.27,0.39,0.53,0.6,0.77l-220.03,192.53c8.77,10.02,18.2,19.45,28.22,28.22L-5.02,5.73c0.25,0.22,0.5,0.42,0.77,0.6l-162.72,242.94c11.02,7.4,22.56,14.07,34.56,19.98L-3.36,6.84c0.29,0.14,0.59,0.27,0.9,0.38l-94.32,276.83c12.5,4.26,25.38,7.7,38.57,10.3L-1.48,7.48c0.32,0.06,0.64,0.1,0.97,0.12l-19.45,291.72C-13.36,299.76-6.71,300,0,300s13.36-0.24,19.95-0.68L0.51,7.6c0.33-0.02,0.66-0.06,0.97-0.12l56.73,286.86c13.19-2.59,26.06-6.04,38.57-10.3L2.46,7.21c0.31-0.11,0.61-0.23,0.9-0.38l129.04,262.41c11.99-5.91,23.53-12.58,34.56-19.98L4.24,6.34c0.27-0.18,0.53-0.39,0.77-0.6l192.53,220.03c10.02-8.77,19.45-18.2,28.22-28.22L5.73,5.02c0.22-0.25,0.42-0.5,0.6-0.77l242.94,162.72c7.4-11.02,14.07-22.56,19.98-34.56L6.84,3.36c0.14-0.29,0.27-0.59,0.38-0.9l276.83,94.32c4.26-12.5,7.7-25.38,10.3-38.57L7.48,1.48c0.06-0.32,0.1-0.64,0.12-0.97l291.72,19.45C299.76,13.36,300,6.71,300,0s-0.24-13.36-0.68-19.95L7.6-0.51C7.58-0.84,7.54-1.16,7.48-1.48z`;

	useEffect(() => {
		if(is_builder) return;
		if(props.main_cta != undefined && props.main_cta !== false) return;
		spinRouletteWithForce();
	}, [ props.main_cta ]);

	useEffect(() => {
		if(!silence) {
			sounds.current.roulette_click_fx = new Howl({ ...getSoundPropsById('roulette_click') });
			sounds.current.end_prize_fx = new Howl({ ...getSoundPropsById('end_applause') });
			sounds.current.end_no_prize_fx = new Howl({ ...getSoundPropsById('oh') });
		}

		detectImagesToLoad();

		loadFontFamilyAndProcessTextWidth();
		window.addEventListener('resize', adaptToContainer);

		return () => {
			window.removeEventListener('resize', adaptToContainer);
			killAllFrameAnimations();
		}
	}, []);

	return (
		<section id="gui_roulette_container" class="roulette_canvas">
			{(is_playable && prize_won !== false && given_prize.current != false) && (
				<div class="roulette_to_success is_fullscreen relative_to_container">
					<div class={(getPrizeType(given_prize.current.prize).text_color.indexOf("FFF") != -1) ? 'is_dark' : 'is_light'} style={{background:getPrizeType(given_prize.current.prize).color}}>
						<Spinner
							color={getPrizeType(given_prize.current.prize).text_color}
						/>
					</div>
				</div>
			)}

			<Stage
				ref={(node) => { refs.current['whole_roulette_ref'] = node; }}
				class="roulette_stage"
				scaleX={0}
				scaleY={0}
				width={canvas_size_w}
				height={canvas_size_h}
				offsetX={canvas_size_w / 2}
				offsetY={canvas_size_h / 2}
				x={canvas_size_w / 2}
				y={canvas_size_h / 2 + y_displacement}
				foo={foo}
			>
				<Layer
					ref={(node) => { refs.current['roulette_ref'] = node; }}
					x={canvas_size_w / 2}
					y={canvas_size_h / 2}
					rotation={whole_roulette_rotation}
				>
					{slices.map((value, index) => {
						const prize = getPrizeType(value.prize);
						if(!prize) return;

						const slice_image = props.indexed_images[`pt_${prize?.id ?? '0'}`];

						const prize_img_scale = (slice_image != undefined) ? radius / slice_image.image_h : 1;
						const slice_real_w = slice_sice.width * radius / ideal_radius;

						return (
							<Group
								key={`slice_${index}`}
								rotation={angle_per_slice * index}
								prize_type={prize.key}
								slice_ref={`slice_${index + 1}`}
								clipFunc={slice_shape_props_clip}
							>
								{(slice_image != undefined) && (
									<Group
										rotation={angle_per_slice / 2}
									>
										<Rect
											width={slice_real_w}
											height={radius}
											x={radius}
											y={-slice_real_w / 2}
											fillPatternImage={slice_image.fillPatternImage}
											fillPatternRepeat='no-repeat'
											fillPatternScaleX={prize_img_scale}
											fillPatternScaleY={prize_img_scale}
											rotation={90}
											hitStrokeWidth={0}
										/>
									</Group>
								)}

								<Rect
									ref={(node) => { refs.current[`slice_${index + 1}`] = node; }}
									width={radius}
									height={radius}
									fill={(slice_image != undefined) ? 'transparent' : prize.color}
									opacity={(slice_image != undefined) ? 0.5 : 1}
									hitStrokeWidth={0}
								/>

								{(slice_image == undefined) && (
									<Text
										fontSize={text_font_size.current - font_sizer}
										lineHeight={1}
										fontStyle={typography_weight}
										fill={prize.text_color}
										fontFamily={typography}
										align="center"
										width={text_box.available_w}
										offsetX={-text_box.delta_ini}
										offsetY={font_offset_y}
										text={prize.name}
										rotation={angle_per_slice / 2}
										hitStrokeWidth={0}
									/>
								)}
							</Group>
						)
					})}

					{slices.map((value, index) => {
						return (
							<Group
								key={`pin_${index}`}
								rotation={angle_per_slice * index}
							>
								<Line
									points={[0, 0, radius, 0]}
									stroke={dark_gray_color}
									strokeWidth={2 * unit}
									hitStrokeWidth={0}
								/>

								<Circle
									radius={pin_radius}
									fill={light_gray_color}
									offsetX={2 * external_ring_delta - radius}
									strokeWidth={2 * unit}
									stroke={dark_gray_color}
									hitStrokeWidth={0}
								/>
							</Group>
						)
					})}
				</Layer>

				<Layer
					x={canvas_size_w / 2}
					y={canvas_size_h / 2}
				>
					{(prize_won !== false && final_prize_image == undefined) && (
						<Group
							ref={(node) => { refs.current['whole_sunburst_ref'] = node; }}
						>
							<Group ref={(node) => { refs.current['sunburst_bg_ref'] = node; }}>
								<Circle
									radius={radius}
									fill={prize_won.text_color}
									hitStrokeWidth={0}
								/>

								<Circle
									radius={radius}
									fill={prize_won.color}
									hitStrokeWidth={0}
								/>
							</Group>

							<Path
								ref={(node) => { refs.current['sunburst_ref'] = node; }}
								data={sunburst_data}
								fillRadialGradientStartPoint={{ x:0, y:0 }}
								fillRadialGradientStartRadius={0}
								fillRadialGradientEndPoint={{ x:0, y:0 }}
								fillRadialGradientEndRadius={radius}
								fillRadialGradientColorStops={[0, prize_won.color, 1, white_color]}
								opacity={(prize_won.key != 0) ? 1 : 0}
								hitStrokeWidth={0}
							/>
						</Group>
					)}

					{(prize_won !== false && final_prize_image != undefined) && (
						<Group
							ref={(node) => { refs.current['given_prize_ref'] = node; }}
							scaleX={0}
							scaleY={0}
						>
							<Group
								clipFunc={full_shape_props_clip}
							>
								<Rect
									width={radius * 2}
									height={radius * 2}
									fillPatternImage={final_prize_image.fillPatternImage}
									fillPatternRepeat='no-repeat'
									fillPatternScaleX={prize_image_scale}
									fillPatternScaleY={prize_image_scale}
									fillPriority='pattern'
									offsetX={radius}
									offsetY={radius}
									hitStrokeWidth={0}
								/>
							</Group>
						</Group>
					)}

					<Group
						ref={(node) => { refs.current['logo_ref'] = node; }}
					>
						<Circle
							radius={logo_radius - 1}
							fill={ui_color}
							shadowColor={black_color}
							shadowBlur={3 * unit}
							shadowOffset={{ x:2 * unit, y:3 * unit }}
							shadowOpacity={0.2}
							shadowForStrokeEnabled={false}
							hitStrokeWidth={0}
						/>

						<Group
							clipFunc={logo_shape_props_clip}
						>
							<Rect
								fill={ui_color}
								width={logo_radius * 2}
								height={logo_radius * 2}
								fillPatternImage={(logo_image != undefined) ? logo_image.fillPatternImage : null}
								fillPatternRepeat='no-repeat'
								fillPatternScaleX={logo_image_scale}
								fillPatternScaleY={logo_image_scale}
								fillPriority={(logo_image != undefined) ? 'pattern' : 'fill'}
								offsetX={logo_radius}
								offsetY={logo_radius}
								hitStrokeWidth={0}
							/>
						</Group>
					</Group>

					<Line
						ref={(node) => { refs.current['active_slice_collider_ref'] = node; }}
						points={[0, -radius, 0, -4 * radius / 5]}
						stroke="transparent"
						strokeWidth={1}
						hitStrokeWidth={3}
					/>

					<Group
						ref={(node) => { refs.current['roulette_ui_ref'] = node; }}
					>
						<Circle
							radius={radius}
							strokeWidth={2 * unit}
							stroke={affect_color_filter(-0.4, ui_color)}
							shadowColor={black_color}
							shadowBlur={unit}
							shadowOffset={{ x:unit, y:2 * unit }}
							shadowOpacity={0.1}
							shadowForStrokeEnabled={false}
							hitStrokeWidth={0}
						/>

						<Group
							x={0}
							y={0}
							rotation={-90}
						>
							{slices.map((value, index) => {
								if(index === 0) return null;

								return (
									<Shape
										key={`triangle_${index}`}
										sceneFunc={triangle_shape_props}
										fill={affect_color_filter(-0.4, ui_color)}
										offsetX={external_ring_delta - radius}
										rotation={angle_per_slice * index}
										strokeWidth={unit}
										stroke={affect_color_filter(-0.7, ui_color)}
										shadowColor={black_color}
										shadowBlur={unit}
										shadowOffset={{ x:unit, y:unit }}
										shadowOpacity={0.3}
										shadowForStrokeEnabled={false}
										hitStrokeWidth={0}
									/>
								)
							})}
						</Group>

						<Circle
							radius={radius + external_ring_delta}
							strokeWidth={external_ring_delta}
							stroke={ui_color}
							shadowColor={black_color}
							shadowBlur={3 * unit}
							shadowOffset={{ x:unit, y:3 * unit }}
							shadowOpacity={0.6}
							shadowForStrokeEnabled={false}
							hitStrokeWidth={0}
						/>

						<Circle
							radius={radius + external_ring_delta / 2}
							strokeWidth={unit}
							stroke={affect_color_filter(-0.5, ui_color)}
							shadowColor={black_color}
							shadowBlur={3 * unit}
							shadowOffset={{ x:unit, y:3 * unit }}
							shadowOpacity={0.2}
							shadowForStrokeEnabled={false}
							hitStrokeWidth={0}
						/>
					</Group>

					<Group y={-1 * (radius + external_ring_delta)}>
						<Path
							ref={(node) => { refs.current['pointer_ref'] = node; }}
							data={pointer_data(3.3 * external_ring_delta)}
							fill={ui_color}
							stroke={affect_color_filter(-0.7, ui_color)}
							strokeWidth={unit}
							rotation={0}
							lineCap="round"
							lineJoin="round"
							shadowColor={black_color}
							shadowBlur={3 * unit}
							shadowOffset={{ x:unit, y:2 * unit }}
							shadowOpacity={0.4}
							shadowForStrokeEnabled={false}
							hitStrokeWidth={0}
						/>

						<Circle
							radius={pin_radius + 4 * unit}
							fill={black_color}
							stroke={black_color}
							strokeWidth={unit}
							hitStrokeWidth={0}
						/>

						<Circle
							radius={pin_radius}
							fill={light_gray_color}
							hitStrokeWidth={0}
						/>
					</Group>

					<Group>
						{light_objects.map((light, l) => {
							if(l === 0) return null;

							return (
								<Circle
									key={`light_${l}`}
									rotation={lights_rotation * l}
									offsetY={radius + external_ring_delta}
									radius={pin_radius - 1}
									fill={affect_color_filter(0.2, ui_color)}
									hitStrokeWidth={0}
								/>
							)
						})}
					</Group>
				</Layer>

				<Layer
					x={canvas_size_w / 2}
					y={canvas_size_h / 2}
					ref={(node) => { refs.current['final_items_ref'] = node; }}
				>
					{((prize_won !== false && display_celebration) || roulette_initial_light_anim != 'none') && (
						<Group
							ref={(node) => { refs.current['lights_on_ref'] = node; }}
							rotation={0}
							opacity={1}
						>
							{light_objects.map((light, l) => {
								const color = (is_final) ? prize_won.color : affect_color_filter(0.7, ui_color);

								let light_color_opacity = ((num_lights + 4) - l) / (num_lights + 4);
								if(!is_final) {
									if(roulette_initial_light_anim == 'single') {
										light_color_opacity = (l == 0) ? 0.5 : 0;
									} else {
										light_color_opacity = (l % 2 == 0) ? 0.5 : 0;
									}
								}

								return (
									<Group
										key={`light_on_${l}`}
										rotation={lights_rotation * l}
										offsetY={radius + external_ring_delta}
										opacity={light_color_opacity}
										hitStrokeWidth={0}
									>
										<Circle
											radius={pin_radius + 5}
											fillRadialGradientStartPoint={{ x: 0, y: 0 }}
											fillRadialGradientStartRadius={0}
											fillRadialGradientEndPoint={{ x: 0, y: 0 }}
											fillRadialGradientEndRadius={pin_radius + 5}
											fillRadialGradientColorStops={[0.5, color, 1, 'rgba(255, 255, 255, 0.1)']}
										/>

										<Circle
											radius={pin_radius + 1}
											fill={color}
											opacity={0.4}
										/>

										<Circle
											radius={pin_radius}
											fill={color}
										/>

										<Circle
											radius={pin_radius - 0.5}
											fillRadialGradientStartPoint={{ x: 0, y: 0 }}
											fillRadialGradientStartRadius={0}
											fillRadialGradientEndPoint={{ x: 0, y: 0 }}
											fillRadialGradientEndRadius={pin_radius + 10}
											fillRadialGradientColorStops={[0, color, 0.5, 'rgba(0, 0, 0, 0.3)', 1, 'rgba(255, 255, 255, 0.7)']}
											opacity={0.5}
										/>

										<Circle
											radius={pin_radius - 2}
											fill={color}
											opacity={0.7}
										/>
									</Group>
								)
							})}
						</Group>
					)}

					{(is_final) && (
						<Group>
							{light_objects.map((light, l) => {
								return (
									<Group
										key={`shine_light_on_${l}`}
										rotation={lights_rotation * l}
										offsetY={radius + external_ring_delta}
										hitStrokeWidth={0}
									>
										<Arc
											innerRadius={pin_radius - 2}
											outerRadius={pin_radius}
											angle={100}
											fill='white'
											opacity={0.7}
											rotation={180 - lights_rotation * l}
										/>
									</Group>
								)
							})}
						</Group>
					)}

					{(prize_won !== false) && (
						<Group
							ref={(node) => { refs.current['prize_text_ref'] = node; }}
							offsetX={radius}
							offsetY={prize_font_offset_y}
							scaleX={0}
							scaleY={0}
							opacity={(hide_final_prize_text) ? 0 : 1}
						>
							<Text
								text={prize_won.name}
								fontSize={won_prize_text_font_size.current}
								fontFamily={typography}
								lineHeight={prize_line_height}
								fontStyle={typography_weight}
								width={2 * radius}
								align="center"
								stroke={black_color}
								strokeWidth={8}
								lineCap="round"
								lineJoin="round"
								hitStrokeWidth={0}
							/>

							<Text
								text={prize_won.name}
								fill={white_color}
								fontSize={won_prize_text_font_size.current}
								fontFamily={typography}
								lineHeight={prize_line_height}
								fontStyle={typography_weight}
								width={2 * radius}
								align="center"
								hitStrokeWidth={0}
							/>
						</Group>
					)}
				</Layer>
			</Stage>

			{(!already_loaded.current) && (
				<Spinner
					color={ui_color}
				>Loading roulette</Spinner>
			)}

			{(is_playable) && (
				<aside
					class={`roulette_buttons${(is_spinning) ? ' is_spinning' : ''}`}
				>
					{(props.main_cta != undefined && props.main_cta !== false) && (
						<div
							class={`spin_button awesome gui_is_animated ${(is_ui_color_light) ? 'is_light' : 'is_dark'}`}
						>{props.main_cta}</div>
					)}

					{((props.main_cta == undefined || props.main_cta === false) && is_builder && !test_enabled) && (
						<div
							class={`spin_button awesome gui_pos_rel_important ${(is_ui_color_light) ? 'is_light' : 'is_dark'}${(is_spinning) ? ' is_spinning' : ''}`}
						>
							<ButtonCollection
								_class={{ other:'flex_button adaptable_button' }}
							>
								<ActionButton
									_fullclass="original_color_item"
									onClick={() => {
										spinRouletteWithForce();
										setTestEnabled(true);
									}}
								>{(isNotEmpty(stage, 'roulette_spin_it_btn_label')) ? stage.roulette_spin_it_btn_label : "Press here"}</ActionButton>
							</ButtonCollection>
						</div>
					)}
				</aside>
			)}
		</section>
	);
});

export default RouletteCanvas;